import { Box, Button, Center, Flex, Grid, Heading, Text, useDisclosure } from '@chakra-ui/react';
import {
  APIKongVX,
  KollectionType,
  PrivateAssetsExtensionType,
  SIGNATURE_MESSAGE_PRIVATE_ASSETS,
} from '@cyberkongz/api-interfaces';
import { Kong } from '@cyberkongz/database';
import {
  Card,
  Checkbox,
  CheckmarkIcon,
  ChevronDownIcon,
  DiscIcon,
  DownloadIcon,
  fetchHttpRaw,
  Field,
  FormButtonsContainer,
  Modal,
  pxToRem,
  TabsContainer,
  TabsItem,
  TRANSITIONS,
  useSharedApiRoutes,
} from '@cyberkongz/ui';
import { useSharedUI } from '@features/sharedUI/SharedUIProvider';
import { chakraComponents, Select } from 'chakra-react-select';
import { useEffect, useState } from 'react';
import { useSignMessage } from 'wagmi';

type DownloadActionTabType = '3d_file' | '3d_print' | 'pfp';

const TABS: { label: string; id: DownloadActionTabType }[] = [
  {
    label: '3D FILE',
    id: '3d_file',
  },
  {
    label: '3D PRINT',
    id: '3d_print',
  },
  {
    label: 'PFP',
    id: 'pfp',
  },
];

type AssetSelectOptions = { label: string; value: PrivateAssetsExtensionType }[];

const OPTIONS_FILE_3D: AssetSelectOptions = [
  { label: '3D Voxel Model File', value: '.vox' },
  { label: '3D GLTF Model File', value: '.gltf' },
  { label: '3D FBX Model File', value: '.fbx' },
];

const OPTIONS_FILE_PFP: AssetSelectOptions = [
  { label: 'Avatar/PFP', value: '_preview_head.png' },
  { label: 'Front Preview', value: '_preview.png' },
  { label: 'Back Preview', value: '_preview_back.png' },
];

// @ts-ignore
const TRANSPARENT_OPTIONS: {
  [key in PrivateAssetsExtensionType]: PrivateAssetsExtensionType;
} = {
  '_preview.png': '_preview_transparent.png',
  '_preview_back.png': '_preview_back_transparent.png',
  '_preview_head.png': '_preview_head_transparent.png',
};

function AssetSelect({
  options,
  selectedAssetType,
  setSelectedAssetType,
}: {
  options: AssetSelectOptions;
  selectedAssetType: PrivateAssetsExtensionType;
  setSelectedAssetType: (assetType: PrivateAssetsExtensionType) => void;
}) {
  return (
    <Box w="100%" cursor="pointer">
      <Select
        isSearchable={false}
        value={options.find((option) => option.value === selectedAssetType)}
        onChange={(option) => (option ? setSelectedAssetType(option.value) : undefined)}
        options={options}
        chakraStyles={{
          control: (provided, { menuIsOpen }) => ({
            ...provided,
            minHeight: pxToRem(52),
            bg: 'transparent',
            borderRadius: 'base',
            borderColor: menuIsOpen ? 'text' : 'neutral.700',
          }),
          menuList: (provided) => ({
            ...provided,
            py: 0,
            border: 'thin',
            bg: 'backgroundSecondary',
            borderRadius: 'base',
            boxShadow:
              '0px 12px 16px -4px rgba(16, 24, 40, 0.1), 0px 4px 6px -2px rgba(16, 24, 40, 0.05);',
          }),
          option: () => ({
            p: 4,
            transform: TRANSITIONS.standard,
            '&:hover': {
              bg: 'backgroundTertiary',
            },
          }),
        }}
        components={{
          DropdownIndicator: (props) => (
            <chakraComponents.DropdownIndicator {...props}>
              <ChevronDownIcon
                css={{
                  width: 4,
                  height: 4,
                  transition: TRANSITIONS.standard,
                  transform: `rotateZ(${props.selectProps.menuIsOpen ? 180 : 0}deg)`,
                  color: 'text',
                }}
              />
            </chakraComponents.DropdownIndicator>
          ),
          Option: (props) => (
            <chakraComponents.Option {...props}>
              <Flex css={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Text>{props.label}</Text>
                <Box>
                  {props.isSelected ? (
                    <CheckmarkIcon css={{ color: 'text', width: pxToRem(14), height: 'auto' }} />
                  ) : null}
                </Box>
              </Flex>
            </chakraComponents.Option>
          ),
        }}
      />
    </Box>
  );
}

function File3DTab({
  selectedAssetType,
  setSelectedAssetType,
}: {
  selectedAssetType: PrivateAssetsExtensionType;
  setSelectedAssetType: (assetType: PrivateAssetsExtensionType) => void;
}) {
  useEffect(() => {
    setSelectedAssetType(OPTIONS_FILE_3D[0].value);
  }, []);

  return (
    <Field label="File Type">
      <AssetSelect {...{ options: OPTIONS_FILE_3D, selectedAssetType, setSelectedAssetType }} />
    </Field>
  );
}

function Print3DTab({
  setSelectedAssetType,
  id,
}: {
  setSelectedAssetType: (assetType: PrivateAssetsExtensionType) => void;
  id: number;
}) {
  useEffect(() => {
    setSelectedAssetType('_for_3d_print.zip');
  }, []);

  return (
    <Box>
      <Card>
        <Flex gap={4} alignItems="center">
          <Center css={{ bg: 'neutral.800', borderRadius: '50%', w: 12, h: 12 }}>
            <DiscIcon color="text" />
          </Center>
          <Text fontWeight="medium">{`${id}_for_3D_print.zip`}</Text>
        </Flex>
      </Card>
    </Box>
  );
}

function PFPTab({
  setSelectedAssetType,
}: {
  setSelectedAssetType: (assetType: PrivateAssetsExtensionType) => void;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [internalSelectedAssetType, setInternalSelectedAssetType] =
    useState<PrivateAssetsExtensionType>('_preview.png');

  useEffect(() => {
    setSelectedAssetType(OPTIONS_FILE_PFP[0].value);
  }, []);

  useEffect(() => {
    setSelectedAssetType(
      isChecked ? TRANSPARENT_OPTIONS[internalSelectedAssetType] : internalSelectedAssetType
    );
  }, [isChecked, internalSelectedAssetType]);

  return (
    <Field label="File Type">
      <AssetSelect
        {...{
          options: OPTIONS_FILE_PFP,
          selectedAssetType: internalSelectedAssetType,
          setSelectedAssetType: setInternalSelectedAssetType,
        }}
      />
      <Checkbox
        checked={isChecked}
        css={{ mt: 4 }}
        onChange={() => {
          setIsChecked(!isChecked);
        }}
      >
        <Text textStyle="p-sm" fontWeight="medium">
          Transparent background
        </Text>
      </Checkbox>
    </Field>
  );
}

export function DownloadActionForm({
  kong,
  type,
}: {
  kong: Kong | APIKongVX;
  type: KollectionType;
}) {
  const { signMessageAsync } = useSignMessage();
  const { open: isOpen, onClose, onOpen } = useDisclosure();
  const [activeTab, setActiveTab] = useState<DownloadActionTabType>('3d_file');
  const [selectedAssetType, setSelectedAssetType] = useState<PrivateAssetsExtensionType>('.vox');
  const { kongVXTheme } = useSharedUI();
  const { SHARED_API_ROUTES } = useSharedApiRoutes();

  async function downloadAsset(assetType: PrivateAssetsExtensionType) {
    const signature = await signMessageAsync({ message: SIGNATURE_MESSAGE_PRIVATE_ASSETS });

    const res = await fetchHttpRaw(SHARED_API_ROUTES.POST_ASSESTS_DOWNLOAD, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        signature,
        id: kong.id,
        assetType,
        kollection: type,
      }),
    });

    if (!res.ok) {
      const response = await res.json();
      window.alert(`Something went wrong: ${response.message}`);
      return;
    }

    res.blob().then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.target = '_blank';
      a.download = `${kong.id}${assetType}`;
      a.click();
    });
  }

  function closeModal() {
    setActiveTab('3d_file');
    setSelectedAssetType('.vox');
    onClose();
  }

  return (
    <>
      <Button
        variant="systemSecondary"
        size="sm"
        onClick={onOpen}
        css={{ borderColor: kongVXTheme?.text, px: 4, '& svg': { height: 4 } }}
      >
        <Flex gap={pxToRem(10)} alignItems="center" ml={pxToRem(-2)} color={kongVXTheme?.text}>
          <DownloadIcon />
          <Text>3D / PFP</Text>
        </Flex>
      </Button>
      <Modal
        variant="form"
        {...{
          isOpen,
          onClose: closeModal,
          headerComponent: <Heading size="xs">3d / pfp</Heading>,
        }}
      >
        <Grid gap={8}>
          <TabsContainer>
            {TABS.map(({ id, label }) => (
              <TabsItem
                key={id}
                {...{
                  activeTab,
                  setActiveTab,
                  tab: id,
                  noShadow: true,
                }}
              >
                {label}
              </TabsItem>
            ))}
          </TabsContainer>
          <Grid gap={6}>
            {activeTab === '3d_file' ? (
              <File3DTab {...{ selectedAssetType, setSelectedAssetType }} />
            ) : null}
            {activeTab === '3d_print' ? (
              <Print3DTab {...{ setSelectedAssetType, id: kong.id }} />
            ) : null}
            {activeTab === 'pfp' ? (
              <PFPTab {...{ selectedAssetType, setSelectedAssetType }} />
            ) : null}
            <FormButtonsContainer>
              <Button variant="systemSecondary" onClick={closeModal}>
                Cancel
              </Button>
              <Button onClick={() => downloadAsset(selectedAssetType)}>Download</Button>
            </FormButtonsContainer>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
}
